import React, { useState } from 'react';
import { usePublicEvents, Event } from '@/hooks/useEvents';
import { Calendar, Clock, MapPin, Euro, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import { fr, enUS, es, de, it, nl, pt, zhCN, ja } from 'date-fns/locale';
import EventDetailPopup from './EventDetailPopup';
import { LanguageCode, MENU_UI_TRANSLATIONS } from '@/constants/menuUITranslations';

interface EventsBannerProps {
  restaurantId: string;
  primaryColor?: string;
  restaurantName?: string;
  currentLanguage?: LanguageCode;
}

interface EventCardProps {
  event: Event;
  primaryColor?: string;
  onClick: () => void;
  currentLanguage: LanguageCode;
}

const getDateLocale = (lang: LanguageCode) => {
  const locales = { fr, en: enUS, es, de, it, nl, pt, zh: zhCN, ja };
  return locales[lang] || fr;
};

const EventCard: React.FC<EventCardProps> = ({ event, primaryColor, onClick, currentLanguage }) => {
  const t = MENU_UI_TRANSLATIONS[currentLanguage];
  const locale = getDateLocale(currentLanguage);

  const formatEventDate = (dateStr: string) => {
    try {
      const [year, month, day] = dateStr.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      if (date.getTime() === today.getTime()) return t.today;
      if (date.getTime() === tomorrow.getTime()) return t.tomorrow;
      return format(date, 'EEEE d MMM', { locale });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (timeStr: string | null) => {
    if (!timeStr) return '';
    return timeStr.slice(0, 5);
  };

  return (
    <button 
      onClick={onClick}
      className="flex-shrink-0 w-72 bg-card rounded-xl shadow-md overflow-hidden border border-border hover:shadow-lg transition-all hover:scale-[1.02] cursor-pointer text-left"
    >
      <div 
        className="h-24 w-full relative"
        style={{ 
          background: event.image_url 
            ? `url(${event.image_url}) center/cover` 
            : `linear-gradient(135deg, ${primaryColor || '#8B5CF6'}20, ${primaryColor || '#8B5CF6'}40)`
        }}
      >
        {event.is_featured && (
          <span 
            className="absolute top-2 right-2 text-xs font-semibold px-2 py-1 rounded-full text-white"
            style={{ backgroundColor: primaryColor || '#8B5CF6' }}
          >
            {t.featured}
          </span>
        )}
        <div className="absolute bottom-2 left-2">
          <span 
            className="text-xs font-semibold px-2 py-1 rounded-full bg-background shadow-sm capitalize"
            style={{ color: primaryColor || '#8B5CF6' }}
          >
            {formatEventDate(event.event_date)}
          </span>
        </div>
      </div>

      <div className="p-4">
        <h3 className="font-bold text-lg mb-1 line-clamp-1">{event.title}</h3>
        
        {event.description && (
          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{event.description}</p>
        )}

        <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
          {event.event_time && (
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatTime(event.event_time)}
              {event.end_time && ` - ${formatTime(event.end_time)}`}
            </span>
          )}
          {event.location && (
            <span className="flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {event.location}
            </span>
          )}
          {event.price && (
            <span 
              className="flex items-center gap-1 font-semibold"
              style={{ color: primaryColor || '#8B5CF6' }}
            >
              <Euro className="h-3 w-3" />
              {event.price}€
            </span>
          )}
        </div>
      </div>
    </button>
  );
};

const EventsBanner: React.FC<EventsBannerProps> = ({ 
  restaurantId, 
  primaryColor, 
  restaurantName,
  currentLanguage = 'fr'
}) => {
  const { events, loading } = usePublicEvents(restaurantId);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const t = MENU_UI_TRANSLATIONS[currentLanguage];

  if (loading || events.length === 0) {
    return null;
  }

  return (
    <>
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3 px-4">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Calendar className="h-5 w-5" style={{ color: primaryColor }} />
            {t.upcomingEvents}
          </h2>
          {events.length > 2 && (
            <a 
              href="#events-section" 
              className="text-sm text-muted-foreground flex items-center gap-1 hover:underline"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('events-section')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              {t.seeAll}
              <ChevronRight className="h-4 w-4" />
            </a>
          )}
        </div>

        <div className="flex gap-4 overflow-x-auto pb-2 px-4 scrollbar-hide">
          {events.map((event) => (
            <EventCard 
              key={event.id} 
              event={event} 
              primaryColor={primaryColor}
              onClick={() => setSelectedEvent(event)}
              currentLanguage={currentLanguage}
            />
          ))}
        </div>
      </div>

      <EventDetailPopup
        event={selectedEvent}
        isOpen={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
        primaryColor={primaryColor}
        restaurantId={restaurantId}
        restaurantName={restaurantName}
        currentLanguage={currentLanguage}
      />
    </>
  );
};

export default EventsBanner;
